import { Box, FormControl, Typography } from '@mui/material'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import SelectInput from '../../Common/SelectInput'
import { fetchAttributesByTemplateId } from '../../../services/entityServices'
import { fetchRelations } from '../../../services/dtServices'
import {
    AttributeType,
    FMTABS,
    IN_TRANSIT_TABS,
    RoutePaths,
    URLSearchParamsKeys,
} from '../../../constants/constants'
import { useTranslation } from 'react-i18next'
import { IAttribute } from '../interfaces'
import { useSearchParams } from 'react-router-dom'

interface RulesTriggerFieldValue {
    value: string
    isRelation: boolean
    dataType: string
    displayName: string
    units: string
}

interface RulesTriggerFieldProps {
    label: string
    id: string
    value: RulesTriggerFieldValue
    onChange: (
        id: string,
        value: RulesTriggerFieldValue,
        type: string,
        isRelation: boolean,
    ) => void
    errorMessage?: string
    required?: boolean
    disabled?: boolean
}

interface RelationItem {
    id: string
    name: string
    templateId: string
    isRelation: boolean
    dataType?: string
}

interface UnitItem {
    id: string
    name: string
}

const RulesTriggerField = (props: RulesTriggerFieldProps): ReactElement => {
    const { t } = useTranslation()
    const { label, id, value, onChange, errorMessage, required, disabled } =
        props

    const org = useAppSelector((state) => state.org.org)
    const user = useAppSelector((state) => state.user.user)
    const unitsList = useAppSelector((state) => state.user.unitsList)
    const selectedEntity = useAppSelector(
        (state) => state.dtStudio.selectedEntity,
    )
    const fmDeviceTemplateId = useAppSelector(
        (state) => state.facilityMonitoring.sensorEntityTemplateId,
    )
    const cclDeviceTemplateId = useAppSelector(
        (state) => state.inTransit.inTransitSettings.deviceEntityTemplateId,
    )

    const dispatch = useAppDispatch()

    const [state, setState] = useState({
        displayTemplateNameSelectInput: true,
        templateName: '',
        selectedAttribute: '',
        unit: '',
        displayName: '',
        selectedAttributeDataType: '',
        isRelAttribute: false,
    })

    const [relationsList, setRelationsList] = useState<RelationItem[]>([])
    const [attributeList, setAttributeList] = useState<IAttribute[]>([])
    const [listOfUnits, setListOfUnits] = useState<UnitItem[]>([])

    const [searchParams] = useSearchParams()
    const tabIndex = searchParams.get(URLSearchParamsKeys.tabIndex)
    const isCCL = globalThis.location.pathname.includes(RoutePaths.ccl)
    const isFM = globalThis.location.pathname.includes(
        RoutePaths.facilityMonitoring,
    )
    const isFMRulesView = isFM && tabIndex === FMTABS.RULES.toString()
    const CCLRulesView = isCCL && tabIndex === IN_TRANSIT_TABS.RULES.toString()

    const getTargetList = useCallback(async () => {
        let relationList: RelationItem[] = []
        if (!org?.id || !selectedEntity.id) {
            return relationList
        }

        dispatch(updateLoader(true))
        try {
            const [attributes, relations] = await Promise.all([
                fetchAttributesByTemplateId(
                    org.id,
                    selectedEntity.id,
                    false,
                    true,
                    false,
                    true,
                ),
                fetchRelations(selectedEntity.id, org.id),
            ])

            const mappedAttributes: RelationItem[] =
                attributes?.map((attr: any) => ({
                    ...attr,
                    templateId: selectedEntity.id,
                    isRelation: false,
                })) || []

            const mappedRelations: RelationItem[] =
                relations?.map((rel: any) => ({
                    ...rel,
                    templateId: rel.childEntity,
                    id: rel.relationName,
                    name: rel.displayName,
                    isRelation: true,
                })) || []

            relationList = [...mappedAttributes, ...mappedRelations]
            return relationList
        } catch (error) {
            console.error('Error fetching target list:', error)
            return relationList
        } finally {
            dispatch(updateLoader(false))
        }
    }, [org?.id, selectedEntity.id, dispatch])
    const getUnitsListAndUnit = useCallback(
        (dataType: string, selectedUnits?: string) => {
            const userUnits = user?.preferences?.units
            let updatedUnits: UnitItem[] = []
            let defaultUnit = ''

            if (dataType === AttributeType.CURRENCY) {
                updatedUnits = [
                    { id: 'USD', name: 'USD' },
                    { id: 'EUR', name: 'EUR' },
                ]
                defaultUnit = 'USD'
            } else {
                const units =
                    unitsList?.find((item) => item.id === dataType)?.units || []
                updatedUnits = units.map((unit: string) => ({
                    id: unit,
                    name: unit,
                }))
                defaultUnit = userUnits?.[dataType] || updatedUnits[0]?.id || ''
            }
            return {
                unitsList: updatedUnits,
                unit: selectedUnits || defaultUnit,
            }
        },
        [user?.preferences?.units, unitsList],
    )

    useEffect(() => {
        getTargetList().then((relationList) => {
            setRelationsList(relationList)

            let templateName = value.value?.split('.')[0] || ''
            const selectedAttribute = value.value?.split('.')[1] || ''
            let displayTemplateNameSelectInput = true
            if (isFMRulesView) {
                templateName =
                    relationList.find(
                        (item) =>
                            item.templateId === fmDeviceTemplateId &&
                            item.isRelation,
                    )?.id || ''
                displayTemplateNameSelectInput = false
            } else if (CCLRulesView) {
                templateName =
                    relationList.find(
                        (item) =>
                            item.templateId === cclDeviceTemplateId &&
                            item.isRelation,
                    )?.id || ''
                displayTemplateNameSelectInput = false
            }

            const newState = {
                ...state,
                displayTemplateNameSelectInput,
                templateName,
                selectedAttribute,
                displayName: value.displayName || '',
                selectedAttributeDataType: value.dataType || '',
                isRelAttribute:
                    value.isRelation || isFMRulesView || CCLRulesView || false,
                unit: value.units || '',
            }
            fetchAttributes(templateName, relationList)
            setState(newState)
            triggerOnChange(newState)

            if (value.dataType) {
                const { unitsList } = getUnitsListAndUnit(
                    value.dataType,
                    value.units,
                )
                setListOfUnits(unitsList)
            }
        })
    }, [])

    const fetchAttributes = async (
        templateName: string,
        relationsList: RelationItem[],
    ) => {
        if (!templateName || !org?.id) {
            setAttributeList([])
            return
        }

        const relation = relationsList.find(
            (item) => item.id === templateName && item.isRelation,
        )

        if (!relation?.templateId) {
            setAttributeList([])
            return
        }

        dispatch(updateLoader(true))
        try {
            const attributes = await fetchAttributesByTemplateId(
                org.id,
                relation.templateId,
                false,
                true,
                true,
                true,
            )
            setAttributeList(attributes || [])
        } catch (error) {
            console.error('Error fetching attributes:', error)
            setAttributeList([])
        } finally {
            dispatch(updateLoader(false))
        }
    }

    const triggerOnChange = useCallback(
        (newState: typeof state) => {
            const valueStr = newState.selectedAttribute
                ? `${newState.templateName}.${newState.selectedAttribute}`
                : newState.templateName

            if (newState.templateName) {
                onChange(
                    id,
                    {
                        value: valueStr,
                        isRelation: newState.isRelAttribute,
                        dataType: newState.selectedAttributeDataType,
                        displayName: newState.displayName,
                        units: newState.unit,
                    },
                    'value',
                    newState.isRelAttribute,
                )
            }
        },
        [id, onChange],
    )

    const onChangeTemplate = useCallback(
        (_: string, tempName: string) => {
            if (state.templateName === tempName) return

            const template = relationsList.find((item) => item.id === tempName)
            const isRelation = template?.isRelation || false

            const { unitsList, unit } = getUnitsListAndUnit(
                isRelation ? '' : template?.dataType || '',
            )
            const newState = {
                ...state,
                templateName: tempName,
                selectedAttribute: '',
                displayName: isRelation ? '' : template?.name || '',
                isRelAttribute: isRelation,
                selectedAttributeDataType: isRelation
                    ? ''
                    : template?.dataType || '',
                unit,
            }

            setState(newState)
            triggerOnChange(newState)
            fetchAttributes(tempName, relationsList)
            setListOfUnits(unitsList)
        },
        [
            state.templateName,
            relationsList,
            getUnitsListAndUnit,
            triggerOnChange,
        ],
    )

    const onChangeSelectedAttribute = useCallback(
        (_: string, attributeId: string) => {
            const attribute = attributeList.find(
                (item) => item.id === attributeId,
            )

            const { unitsList, unit } = getUnitsListAndUnit(
                attribute?.dataType || '',
            )

            const newState = {
                ...state,
                selectedAttribute: attributeId,
                displayName: attribute?.name || '',
                isRelAttribute: true,
                selectedAttributeDataType: attribute?.dataType || '',
                unit,
            }

            setState(newState)
            triggerOnChange(newState)
            setListOfUnits(unitsList)
        },
        [attributeList, getUnitsListAndUnit, triggerOnChange, state],
    )

    const onChangeUnits = useCallback(
        (_: string, units: string) => {
            const newState = { ...state, unit: units }
            setState(newState)
            triggerOnChange(newState)
        },
        [state, triggerOnChange],
    )

    return (
        <FormControl fullWidth disabled={!!disabled} size="small">
            <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                {label}
                {required ? '*' : ''}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                {state.displayTemplateNameSelectInput && (
                    <Box minWidth="calc(50% - 40px)" width="calc(100%)">
                        <SelectInput
                            label=""
                            id={id}
                            value={state.templateName}
                            onChange={onChangeTemplate}
                            list={relationsList}
                            errorMessage={
                                !state.templateName && errorMessage
                                    ? errorMessage
                                    : ''
                            }
                            disabled={disabled}
                        />
                    </Box>
                )}
                {state.isRelAttribute && (
                    <Box minWidth="calc(50% - 40px)" width="100%">
                        <SelectInput
                            label=""
                            id={`${id}-attribute-selection`}
                            value={state.selectedAttribute}
                            onChange={onChangeSelectedAttribute}
                            list={attributeList}
                            errorMessage={
                                state.isRelAttribute &&
                                !state.selectedAttribute &&
                                errorMessage
                                    ? t('required')
                                    : ''
                            }
                            disabled={disabled}
                        />
                    </Box>
                )}
                {listOfUnits.length > 0 && (
                    <Box sx={{ maxWidth: '76px' }}>
                        <SelectInput
                            id={`${id}-units-selection`}
                            label=""
                            value={state.unit}
                            onChange={onChangeUnits}
                            errorMessage={
                                !state.unit && errorMessage ? t('required') : ''
                            }
                            list={listOfUnits}
                            hasEmpty={false}
                            disabled={disabled}
                        />
                    </Box>
                )}
            </Box>
        </FormControl>
    )
}

export default RulesTriggerField
